/**
 * Tier-2 glow: the main pass renders colour + emissive (MRT) into a full-res target; the emissive is blurred with
 * a dual-Kawase chain (½ → ¼ → ⅛ → ¼ → ½) and the composite pass adds it back while writing to the screen.
 */
import { bindTextureUnit, createProgram, createTarget, destroyTarget, type FullscreenQuad, type Program, type Target } from './gl';
import { COMPOSITE_FRAG, KAWASE_DOWN_FRAG, KAWASE_UP_FRAG } from './shaders/bloom';
import { QUAD_VERT } from './shaders/quad';

export class BloomChain {
  private readonly gl: WebGL2RenderingContext;
  private readonly quad: FullscreenQuad;
  private readonly down: Program;
  private readonly up: Program;
  private readonly composite: Program;
  /** full-res MRT scene target: [colour, emissive] */
  scene: Target | null = null;
  private mips: Target[] = [];

  constructor(gl: WebGL2RenderingContext, quad: FullscreenQuad) {
    this.gl = gl;
    this.quad = quad;
    this.down = createProgram(gl, QUAD_VERT, KAWASE_DOWN_FRAG);
    this.up = createProgram(gl, QUAD_VERT, KAWASE_UP_FRAG);
    this.composite = createProgram(gl, QUAD_VERT, COMPOSITE_FRAG);
  }

  resize(devWidth: number, devHeight: number): void {
    const gl = this.gl;
    if (this.scene && this.scene.width === devWidth && this.scene.height === devHeight) return;
    this.release();
    const rgba = { internalFormat: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE, filter: gl.LINEAR };
    this.scene = createTarget(gl, devWidth, devHeight, { ...rgba, count: 2 });
    this.mips = [2, 4, 8].map((div) => createTarget(gl, Math.max(1, Math.round(devWidth / div)), Math.max(1, Math.round(devHeight / div)), rgba));
  }

  /** Blur the emissive attachment and composite colour + bloom to the default framebuffer. */
  resolve(devWidth: number, devHeight: number, strength: number): void {
    const gl = this.gl;
    const scene = this.scene;
    if (!scene || this.mips.length !== 3) return;
    const [m2, m4, m8] = this.mips as [Target, Target, Target];
    // down: emissive → ½ → ¼ → ⅛
    this.down.use();
    this.blit(this.down, scene.textures[1]!, scene.width, scene.height, m2);
    this.blit(this.down, m2.textures[0]!, m2.width, m2.height, m4);
    this.blit(this.down, m4.textures[0]!, m4.width, m4.height, m8);
    // up: ⅛ → ¼ → ½
    this.up.use();
    this.blit(this.up, m8.textures[0]!, m8.width, m8.height, m4);
    this.blit(this.up, m4.textures[0]!, m4.width, m4.height, m2);
    // composite to screen
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, devWidth, devHeight);
    this.composite.use();
    bindTextureUnit(gl, 0, scene.textures[0]!, this.composite.loc('uColor'));
    bindTextureUnit(gl, 1, m2.textures[0]!, this.composite.loc('uBloom'));
    gl.uniform1f(this.composite.loc('uStrength'), strength);
    this.quad.draw();
  }

  private blit(prog: Program, src: WebGLTexture, srcW: number, srcH: number, dst: Target): void {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fb);
    gl.viewport(0, 0, dst.width, dst.height);
    bindTextureUnit(gl, 0, src, prog.loc('uSrc'));
    gl.uniform2f(prog.loc('uHalfTexel'), 0.5 / srcW, 0.5 / srcH);
    this.quad.draw();
  }

  private release(): void {
    destroyTarget(this.gl, this.scene);
    this.scene = null;
    for (const m of this.mips) destroyTarget(this.gl, m);
    this.mips = [];
  }

  destroy(): void {
    this.release();
    this.down.destroy();
    this.up.destroy();
    this.composite.destroy();
  }
}
